import React, {useEffect, useState} from 'react';
import {DropzoneArea} from "material-ui-dropzone";
import {useTranslation} from "react-i18next";
import {deleteFromGCP, uploadToGCP} from "../../utils";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import {ADD_NOTIFICATION} from "../../store/action";
import makeStyles from "@material-ui/core/styles/makeStyles";
import {useConfirmDialog} from "../Dialog";
import CropImages from "./CropImages";
import {connect} from "react-redux";
import BackupIcon from '@material-ui/icons/Backup';
import {styled} from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import DeleteIcon from '@material-ui/icons/Delete';
import ButtonBase from "@material-ui/core/ButtonBase";

export const StyledImage = styled(Box)((props) => ({
    backgroundImage: `url(${props.src})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    height: '55px',
    width: '100px',
    maxWidth: '500px',
}))

const useStyles = makeStyles((theme) => ({
    dropZoneCustomClass: {
        minHeight: '125px',
        'MuiDropzoneArea-icon': {
            fill: theme.palette.primary
        },
    },
    previewImages: {
        display: 'flex',
        flexWrap: 'wrap',
        paddingTop: theme.spacing(1),
        gap: '5px'
    },
    previewImage: {
        width: '100px',
        position: 'relative',
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: '5px'
    },
    deleteIcon: {
        position: 'absolute',
        top: '5px',
        right: '5px',
        fontSize: '20px'
    }

}));
/**
 * Enhanced image reading function optimized for mobile devices
 * Uses object URLs instead of data URLs for better performance
 */
const readImage = (file) => {
    return new Promise((res, rej) => {
        // For mobile devices, use object URLs for better performance
        // Object URLs are more memory efficient than data URLs
        try {
            const objectUrl = URL.createObjectURL(file);
            res(objectUrl);
        } catch (error) {
            // Fallback to FileReader if object URL creation fails
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                res(reader.result);
            });
            reader.addEventListener('error', () => {
                rej('cant read image');
            });
            reader.readAsDataURL(file);
        }
    });
}

const runSync = (iterable, callback, index = 0) => {
    if (!iterable[index]) {
        return
    }
    callback(iterable[index]).finally(() => {
        runSync(iterable, callback, ++index)
    })
}

const FileUpload = ({input: {value, onChange}, dispatch, setLoading}) => {
    const [images, setImages] = useState(value)
    const [objectUrls, setObjectUrls] = useState([]) // Track object URLs for cleanup
    const classes = useStyles();
    const {DialogComp, handleClickOpen} = useConfirmDialog(CropImages)

    useEffect(() => {
        onChange(images)
    }, [images, onChange])

    // Cleanup object URLs when component unmounts
    useEffect(() => {
        return () => {
            objectUrls.forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [objectUrls]);

    const uuid = firebase.auth().currentUser?.uid

    const {t} = useTranslation()

    return <><DropzoneArea
        acceptedFiles={['image/*']}
        dropzoneText={t("drag_image_or_click")}
        dropzoneClass={classes.dropZoneCustomClass}
        onDrop={(files, event) => {
            runSync(files, async file => {
                const stopLoading = setLoading();
                try {
                    const imageUrl = await readImage(file)
                    
                    // Track object URL for cleanup
                    if (imageUrl && imageUrl.startsWith('blob:')) {
                        setObjectUrls(prev => [...prev, imageUrl]);
                    }
                    
                    const croppedImage = await handleClickOpen({src: imageUrl})
                    if (!uuid) {
                        dispatch({
                            type: ADD_NOTIFICATION,
                            payload: {
                                type: 'error',
                                text: t('general-error')
                            }
                        })
                    }
                    if (croppedImage) {
                        const uploadedFile = await uploadToGCP(croppedImage, uuid)
                        setImages(prevImages => prevImages.concat([uploadedFile]))
                    }
                    
                    // Clean up the object URL after processing
                    if (imageUrl && imageUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(imageUrl);
                        setObjectUrls(prev => prev.filter(url => url !== imageUrl));
                    }
                    
                    stopLoading()
                    return true
                } catch (e) {
                    stopLoading()
                    dispatch({
                        type: ADD_NOTIFICATION,
                        payload: {
                            type: 'error',
                            text: t('could-not-upload') + file.name
                        }
                    })
                    return false
                }
            })

        }}
        color={'primary'}
        filesLimit={5}
        Icon={() => <BackupIcon size={'big'} color={'primary'}/>}
        multiple={true}
        showAlerts={['error']}
        maxFileSize={6000000}
        getFileLimitExceedMessage={(filesLimit) => t(`max-file-exceeded`) + filesLimit}
        getDropRejectMessage={(rejectedFile) => t(`could-not-upload`) + rejectedFile}
        showPreviewsInDropzone={false}
    />
        <div className={classes.previewImages}>
            {images.map(image => {
                return <div className={classes.previewImage} key={image.name}>
                    <ButtonBase className={classes.deleteIcon} onClick={async (file) => {
                        const stopLoading = setLoading();
                        try {
                            if (uuid) {
                                const isDeleted = await deleteFromGCP(image.name, uuid)

                                if (isDeleted) {
                                    setImages(prevImages => prevImages.filter(img => image.name !== img.name))
                                }
                            }
                            stopLoading()
                        } catch (e) {
                            stopLoading()
                            dispatch({
                                type: ADD_NOTIFICATION,
                                payload: {
                                    type: 'error',
                                    text: t('could-not-delete') + file.name
                                }
                            })
                        }
                    }}>
                        <DeleteIcon fontSize={'inherit'} color={'primary'}/>
                    </ButtonBase>
                    <StyledImage src={image.url}/>
                </div>
            })}
        </div>
        {DialogComp}
    </>
}

export default connect()(FileUpload);